
import React, { useState, useEffect, useCallback } from "react";
import { useMeta } from "@/contexts/MetaContext";
import { useToast } from "@/components/ui/use-toast";
import { Check, ChevronDown, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { calculateCampaignHealthScore } from "@/services/metaApi/metrics";
import { trackSuccessfulRequest } from "@/utils/metaApiRateLimiting";

// Cache key for localStorage
const CAMPAIGNS_CACHE_KEY = 'meta_campaigns_cache';

export function CampaignSelector() {
  const [open, setOpen] = useState(false);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [campaignName, setCampaignName] = useState<string>("Wähle Kampagne");
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const { accessToken, selectedCampaignId, setSelectedCampaignId } = useMeta();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  
  // Load cached campaigns data
  useEffect(() => {
    try {
      const cachedData = localStorage.getItem(CAMPAIGNS_CACHE_KEY);
      if (cachedData) {
        const { campaigns: cachedCampaigns, timestamp } = JSON.parse(cachedData);
        if (cachedCampaigns?.length > 0) {
          setCampaigns(cachedCampaigns);
          setLastFetchTime(timestamp || Date.now());
          console.log("Using cached campaigns data:", cachedCampaigns.length, "campaigns");
        }
      }
    } catch (e) {
      console.error("Error loading cached campaigns data:", e);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      // Only fetch if we haven't fetched in the last 5 minutes or if there's no data
      const now = Date.now();
      const cacheDuration = 5 * 60 * 1000; // 5 minutes
      
      if (now - lastFetchTime < cacheDuration && campaigns.length > 0 && lastFetchTime !== 0) {
        console.log("Using cached campaign data (refresh interval: 5 minutes)");
        return;
      }
      
      setLoading(true);
      setError(null);
      
      // Get stored ads data
      let topAdsData = [];
      try {
        const storedData = localStorage.getItem('top_ads_data');
        if (storedData) {
          topAdsData = JSON.parse(storedData);
        }
      } catch (e) {
        console.error("Error parsing stored ads data:", e);
      }
      
      // If no stored data, generate sample data
      if (!topAdsData || topAdsData.length === 0) {
        try {
          // Import dynamically to avoid circular references
          const { getSampleAdData } = await import("../dashboard/top-ads/adDataUtils");
          topAdsData = getSampleAdData();
          localStorage.setItem('top_ads_data', JSON.stringify(topAdsData));
        } catch (err) {
          console.error("Error generating sample data:", err);
          setError("Fehler beim Laden der Kampagnen");
          return;
        }
      }
      
      // Update state with campaigns data
      setCampaigns(topAdsData);
      setLastFetchTime(now);
      
      // Save to cache
      try {
        localStorage.setItem(CAMPAIGNS_CACHE_KEY, JSON.stringify({
          campaigns: topAdsData,
          timestamp: now
        }));
      } catch (e) {
        console.error("Error saving campaigns to cache:", e);
      }
      
      // Track this as a successful API request for rate limiting
      trackSuccessfulRequest('meta_campaigns');
      
      console.log("Campaigns loaded:", topAdsData.length);
      
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setError("Fehler beim Laden der Kampagnen");
      
      toast({
        title: "Fehler beim Laden der Kampagnen",
        description: error instanceof Error ? error.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [campaigns, lastFetchTime, toast]);

  // Fetch campaigns when the component mounts
  useEffect(() => {
    if (accessToken) {
      fetchCampaigns();
    }
  }, [accessToken, fetchCampaigns]);

  // Load selected campaign name when component mounts or campaigns/selectedId changes
  useEffect(() => {
    if (selectedCampaignId && campaigns.length > 0) {
      const campaign = campaigns.find(camp => camp.id === selectedCampaignId);
      if (campaign) {
        setCampaignName(campaign.name);
      } else if (selectedCampaignId) {
        // If the selected campaign isn't in our list but we have campaigns and an ID
        console.log("Selected campaign not found in campaigns list, resetting selection");
        setSelectedCampaignId(campaigns[0]?.id || null);
      }
    } else if (campaigns.length > 0 && !selectedCampaignId) {
      // Auto-select first campaign if none selected
      setSelectedCampaignId(campaigns[0].id);
      setCampaignName(campaigns[0].name);
    }
  }, [selectedCampaignId, campaigns, setSelectedCampaignId]);

  const handleSelectCampaign = useCallback((campaignId: string) => {
    // First check if this is already the selected campaign
    if (selectedCampaignId === campaignId) {
      setOpen(false);
      return; // Don't do anything else if already selected
    }
    
    // Find the selected campaign
    const selectedCampaign = campaigns.find(camp => camp.id === campaignId);
    
    if (selectedCampaign) {
      setCampaignName(selectedCampaign.name);
      
      // Use a short delay before updating the selected campaign ID
      // This gives the UI time to update before potentially triggering API calls
      setTimeout(() => {
        setSelectedCampaignId(campaignId);
        localStorage.setItem('meta_selected_campaign_id', campaignId);
      
        // Don't show toast on automatic selection (silent)
        if (campaignId !== campaigns[0]?.id || selectedCampaignId) {
          toast({
            title: "Kampagne ausgewählt",
            description: `${selectedCampaign.name} wurde ausgewählt.`,
            variant: "default",
          });
        }
      }, 100);
    }
    
    setOpen(false);
  }, [campaigns, setSelectedCampaignId, toast, selectedCampaignId]);

  const renderHealthIndicator = (campaign: any) => {
    try {
      const healthScore = calculateCampaignHealthScore(campaign);
      
      if (healthScore >= 80) {
        return <CheckCircle className="ml-2 h-4 w-4 text-green-500" />;
      } else if (healthScore >= 50) {
        return <AlertCircle className="ml-2 h-4 w-4 text-amber-500" />;
      } else {
        return <XCircle className="ml-2 h-4 w-4 text-red-500" />;
      }
    } catch (e) {
      console.error("Error calculating health score:", e);
      return null;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between border-gray-700"
          disabled={loading || !accessToken}
        >
          {loading ? "Lädt..." : error ? "Fehler" : campaignName}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        {error ? (
          <div className="p-4 text-center">
            <AlertCircle className="mx-auto h-6 w-6 text-red-500 mb-2" />
            <p className="text-sm text-red-600 mb-2">{error}</p>
            <Button 
              variant="outline" 
              size="sm"
              className="w-full"
              onClick={() => fetchCampaigns()}
            >
              Erneut versuchen
            </Button>
          </div>
        ) : (
          <Command>
            <CommandInput placeholder="Kampagne suchen..." />
            <CommandList>
              <CommandEmpty>Keine Kampagnen gefunden.</CommandEmpty>
              <CommandGroup>
                {campaigns.map((campaign) => (
                  <CommandItem
                    key={campaign.id}
                    value={campaign.id}
                    onSelect={() => handleSelectCampaign(campaign.id)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      {campaign.name.length > 20
                        ? campaign.name.substring(0, 20) + "..."
                        : campaign.name}
                      {renderHealthIndicator(campaign)}
                    </div>
                    {campaign.id === selectedCampaignId && (
                      <Check className="h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
